import React from "react";
import { motion } from "framer-motion";
import { STATS, ASSETS } from "../data/entrepminds";

const About = () => {
  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="grid lg:grid-cols-12 gap-12 items-end mb-16"
        >
          <div className="lg:col-span-7">
            <div className="section-eyebrow mb-4">About Entrepminds</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05]">
              Not another report.
              <br />
              <span className="gold-text">A working transformation system.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#6b6356] text-base md:text-lg leading-relaxed">
            Entrepminds is a Dubai-based consultancy that builds, integrates and runs the
            growth engine for ambitious businesses — combining strategy, software,
            artificial intelligence and performance marketing into one ecosystem.
          </div>
        </motion.div>

        {/* Bento grid */}
        <div className="grid lg:grid-cols-12 grid-rows-[repeat(auto,minmax(0,1fr))] gap-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="em-card p-8 lg:col-span-5 lg:row-span-2"
          >
            <div className="font-mono-em text-xs tracking-[0.25em] uppercase text-[#8a6a10] mb-3">
              Mission
            </div>
            <p className="text-[#0a0a0a] font-display text-2xl leading-snug">
              Empower businesses through customized GrowthOS™ blueprints that combine
              strategy, technology, AI, automation and operational excellence into one
              integrated growth ecosystem.
            </p>
            <div className="gold-divider my-8" />
            <div className="font-mono-em text-xs tracking-[0.25em] uppercase text-[#8a6a10] mb-3">
              Vision
            </div>
            <p className="text-[#3f3a30] text-base leading-relaxed">
              To become the leading provider of AI-powered business transformation
              systems that help companies operate smarter, scale faster, and compete
              successfully in the digital economy.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7 relative rounded-2xl overflow-hidden border border-[#d4af37]/35 min-h-[260px]"
          >
            <img
              src={ASSETS.dubai}
              alt="Dubai skyline"
              className="w-full h-full object-cover absolute inset-0 grayscale hover:grayscale-0 transition-all duration-1000"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#faf7ee] via-[#faf7ee]/60 to-transparent" />
            <div className="relative h-full p-8 flex flex-col justify-end">
              <div className="font-mono-em text-xs tracking-[0.25em] uppercase text-[#8a6a10]">
                HQ · Business Bay
              </div>
              <h3 className="font-display text-3xl mt-2 max-w-md">
                Built in <span className="gold-text">Dubai</span> for the AI economy.
              </h3>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="em-card p-8 lg:col-span-7"
          >
            <div className="font-mono-em text-xs tracking-[0.25em] uppercase text-[#8a6a10] mb-3">
              The Problem We Solve
            </div>
            <p className="text-[#3f3a30] text-base md:text-lg leading-relaxed">
              Most businesses bleed revenue through slow lead-flow, broken processes,
              disconnected tools and zero automation. Reports don&apos;t fix this — only a
              system does.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm text-[#6b6356]">
              {[
                "Revenue leaks & broken funnels",
                "Disconnected CRM, ops & finance",
                "No automation, no leverage",
                "Marketing without measurable ROI",
              ].map((p) => (
                <div key={p} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                  {p}
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {STATS.map((s) => (
            <div
              key={s.label}
              data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="border-t border-[#d4af37]/35 pt-5"
            >
              <div className="font-display text-5xl md:text-6xl font-semibold">
                <span className="gold-text">{s.value}</span>
                <span className="text-[#0a0a0a]">{s.suffix}</span>
              </div>
              <div className="mt-2 text-sm text-[#6b6356] uppercase tracking-wider">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
